/* eslint-disable id-length, perfectionist/sort-objects */
import {
    ColorSchemeHelper,
    ColorScheme,
    RGB,
    sanitisePercent,
    sanitiseOpacity,
    parsePercent,
    parseOpacity,
    RGBHelper,
} from './common';

/**
 * Represents a color in CMYK color space.
 * Cyan, Magenta, Yellow and Black values are expressed as decimals between 0 and 1.
 */
export interface CMYK {
    c: number;
    m: number;
    y: number;
    k: number;
    a?: number;
}

export class CMYKHelper implements ColorSchemeHelper<CMYK> {
    public scheme: ColorScheme = 'cmyk';

    private rgbHelper = new RGBHelper();

    public isMatch(color: object): color is CMYK {
        return 'c' in color && 'm' in color && 'y' in color && 'k' in color;
    }

    public sanitise(color: CMYK): CMYK {
        return {
            c: sanitisePercent(color.c),
            m: sanitisePercent(color.m),
            y: sanitisePercent(color.y),
            k: sanitisePercent(color.k),
            a: sanitiseOpacity(color.a),
        };
    }

    public parse(color: string): CMYK | undefined {
        const patterns = [
            /(?:device-)?cmyk\(\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%?)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%?)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%?)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%?)\s*\)/i,
            /(?:device-)?cmyka?\(\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%?)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%?)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%?)\s*,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+)%?)\s*(?:,\s*((?:(?:\d+(?:\.\d+)?)|\.\d+))\s*)\)/i,
        ];

        const match = patterns.map(pattern => pattern.exec(color)).filter(m => !!m)[0];

        if (!match) {
            return undefined;
        }

        const result = this.sanitise({
            c: parsePercent(match[1]),
            m: parsePercent(match[2]),
            y: parsePercent(match[3]),
            k: parsePercent(match[4]),
            a: parseOpacity(match[5]),
        });

        return result;
    }

    public toString(color: CMYK): string {
        if (color.a === 1 || color.a === undefined) {
            return `device-cmyk(${color.c}, ${color.m}, ${color.y}, ${color.k})`;
        }

        return `device-cmyka(${color.c}, ${color.m}, ${color.y}, ${color.k}, ${color.a})`;
    }

    public fromRgb(rgb: RGB): CMYK {
        const { a, b, g, r } = rgb;

        let c = 0;
        let m = 0;
        let y = 0;
        let k = 0;

        if (r === 0 && g === 0 && b === 0) {
            return { c: 0, m: 0, y: 0, k: 1, a };
        }

        c = 1 - r / 255;
        m = 1 - g / 255;
        y = 1 - b / 255;

        const minCMY = Math.min(c, Math.min(m, y));
        c = (c - minCMY) / (1 - minCMY);
        m = (m - minCMY) / (1 - minCMY);
        y = (y - minCMY) / (1 - minCMY);

        k = minCMY;

        const result = this.sanitise({ c, m, y, k, a });

        return result;
    }

    public toRgb(cmyk: CMYK, options?: { round?: boolean }): RGB {
        const { a, c, k, m, y } = cmyk;

        let r: number;
        let g: number;
        let b: number;

        r = 1 - Math.min(1, c * (1 - k) + k);
        g = 1 - Math.min(1, m * (1 - k) + k);
        b = 1 - Math.min(1, y * (1 - k) + k);

        let round = true;
        if (options && options.round !== undefined) {
            round = options.round;
        }

        r *= 255;
        g *= 255;
        b *= 255;

        const result = this.rgbHelper.sanitise({ r, g, b, a }, round);

        return result;
    }

    public areEqual(colorA: CMYK, colorB: CMYK): boolean {
        const aIsNull = colorA === null;

        const bIsNull = colorB === null;

        if (aIsNull || bIsNull) {
            return aIsNull === bIsNull;
        }

        const aIsOpaque = colorA.a === 1 || colorA.a === undefined;
        const bIsOpaque = colorB.a === 1 || colorB.a === undefined;

        const areEqual =
            colorA.c === colorB.c &&
            colorA.m === colorB.m &&
            colorA.y === colorB.y &&
            colorA.k === colorB.k &&
            (colorA.a === colorB.a || aIsOpaque === bIsOpaque);

        return areEqual;
    }
}
